import { ICategoryDomainValue } from 'models/common';
import * as c from 'models/constants';
import { IProduct, IProductForm } from 'models/product/product';
import { domainValuesMap } from 'redux/modules/domainvalues';
import { uniq, upperFirstObjectPropertiesNames } from './common';

export const categoryToAttributes = {};

export function mapErrorKeyToMessage(key: string, t: (key: string) => string) {
	switch (key) {
		case 'name':
			return t('Shop_ManageShop_Products_CreateProduct_Label_Title');

		case 'sku':
			return t('Shop_ManageShop_Products_CreateProduct_Label_SKU');

		case 'price':
			return t('Shop_ManageShop_Products_CreateProduct_Label_Price');

		case 'discountedPrice':
			return t('Shop_ManageShop_Products_CreateProduct_Label_SalePrice');

		case 'unitsInStock':
			return t('Shop_ManageShop_Products_CreateProduct_Label_Quantity');

		case 'externalLink':
			return t('Shop_ManageShop_Products_CreateProduct_Label_ExternalLink');

		case 'firstDropdownCategoryKey':
		case 'secondDropdownCategoryKey':
			return t('Shop_ManageShop_Products_CreateProduct_Label_Category');

		case 'description':
			return t('Shop_ManageShop_Products_CreateProduct_Label_Details');

		case 'shortDescription':
			return t('Shop_ManageShop_Products_CreateProduct_Label_ShortDescription');

		case 'composition':
			return t('Shop_ManageShop_Products_CreateProduct_Label_MaterialAndCare');

		case 'delivery':
			return t('Shop_ManageShop_Products_CreateProduct_Label_ShippingInfo');

		default:
			return key;
	}
}

type CategoryToAttribute = { categories: number[]; attributes: c.AttributeName[] };

export const categoryToAttributesMap: CategoryToAttribute[] = [
	{
		categories: [c.SOME_CATEGORY_KEYS.HAIRBEARD_HAIR],
		attributes: [c.ATTRIBUTES_KEYS.HAIR_TREATMENTS, c.ATTRIBUTES_KEYS.HAIR_TYPE, c.ATTRIBUTES_KEYS.HAIR_LENGTH],
	},
	{
		categories: [
			c.SOME_CATEGORY_KEYS.SKINCARE_MOISTURIZERS,
			c.SOME_CATEGORY_KEYS.SKINCARE_CLEANSERS,
			c.SOME_CATEGORY_KEYS.SKINCARE_MASKS,
			c.SOME_CATEGORY_KEYS.SKINCARE_SUNSCREEN,
		],
		attributes: [c.ATTRIBUTES_KEYS.SKIN_TREATMENTS],
	},
];

export const categoryToAttributesMapVariations: CategoryToAttribute[] = [
	{
		categories: [
			c.SOME_CATEGORY_KEYS.HAIRBEARD_HAIR,
			c.SOME_CATEGORY_KEYS.MAKEUP_FACE,
			c.SOME_CATEGORY_KEYS.MAKEUP_EYES,
			c.SOME_CATEGORY_KEYS.MAKEUP_LIP,
			c.SOME_CATEGORY_KEYS.MAKEUP_NAILS,
		],
		attributes: [c.ATTRIBUTES_KEYS.COLOUR_NAME, c.ATTRIBUTES_KEYS.COLOUR],
	},
	{
		categories: [c.SOME_CATEGORY_KEYS.NUTRITION],
		attributes: [c.ATTRIBUTES_KEYS.FLAVOUR_NAME, c.ATTRIBUTES_KEYS.FLAVOUR],
	},
];

type AttributeRenderDescription = { label: string; prefix?: string; domainValue?: keyof typeof domainValuesMap };

export const attributesDefinitions: { [key in c.AttributeName]: AttributeRenderDescription } = {
	HairTreatment: { label: 'Shop_ManageShop_Products_CreateProduct_Label_HairTreatment' },
	HairType: {
		label: 'Shop_ManageShop_Products_CreateProduct_Label_HairType',
		prefix: 'DV_HairTypes_',
		domainValue: 'hairType',
	},
	HairLength: {
		label: 'Shop_ManageShop_Products_CreateProduct_Label_HairLength',
		prefix: 'DV_HairLengths_',
		domainValue: 'hairLength',
	},
	SkinTreatment: {
		label: 'Shop_ManageShop_Products_CreateProduct_Label_SkinTreatment',
		prefix: 'DV_SkinProblems_',
		domainValue: 'skinProblems',
	},
	Colour: { label: 'Shop_ManageShop_Products_CreateProduct_Label_Color', prefix: 'DV_Colors_', domainValue: 'colors' },
	ColourName: { label: 'Shop_ManageShop_Products_CreateProduct_Label_ColorName' },
	Flavour: {
		label: 'Shop_ManageShop_Products_CreateProduct_Label_Flavour',
		prefix: 'DV_Flavours_',
		domainValue: 'flavours',
	},
	FlavourName: { label: 'Shop_ManageShop_Products_CreateProduct_Label_FlavourName' },
};

export const attributesToShow = (form: IProductForm, section: 'main' | 'variations'): c.AttributeName[] => {
	const tryCategories = [
		form.firstDropdownCategoryKey,
		form.secondDropdownCategoryKey,
		form.thirdDropdownCategoryKey,
	].map(Number);
	let ret: c.AttributeName[] = [];
	if (!tryCategories.length) {
		return ret;
	}
	const workingMap = section === 'main' ? categoryToAttributesMap : categoryToAttributesMapVariations;
	for (const ctam of workingMap) {
		for (const cat of tryCategories) {
			if (ctam.categories.includes(cat)) {
				ret = [...ret, ...ctam.attributes];
			}
		}
	}
	return uniq(ret);
};

export const contentUnit = (category: number): 'g' | 'ml' | undefined => {
	category = Number(category);
	const contentInMl: Array<IProduct['attributes']['CategoryKey']> = [
		2, // Hair&Beard -> Hair
		3,
		4,
		5,
		6,
		7,
		8,
		9,
		10,
		11,
		12,
		13,
		14, // Hair&Beard -> Beard
		15,
		16,
		17,
		19,
		20,
		21,
		22,
		25, // Makeup -> Face
		26,
		27,
		28,
		34,
		35, // Makeup -> Eye
		36,
		39,
		40,
		42,
		43,
		45, // Makeup-> Lip
		47,
		50,
		51,
		53, // Makeup -> Nails
		54,
		55,
		56,
		58, // Fragrance -> Perfume
		59,
		60,
		62,
		63, // Fragrance -> Wellness
		65,
		66,
		68,
		70, // Skincare -> Moisturizers
		71,
		72,
		73,
		74,
		75, // Skincare -> Cleansers
		76,
		77,
		77,
		80,
		81, // Skincare -> Masks
		82,
		84,
		85, // Skincare -> Sunscreen
		86,
		87,
		88,
		89,
		120,
		125,
		130,
		132,
		153, // Pet Beauty -> Bathing
		154,
		155,
		156,
		157,
	];
	const noContent: Array<IProduct['attributes']['CategoryKey']> = [
		91, // Tools -> Hair
		92,
		93,
		94,
		95,
		96,
		97,
		98, // Tools -> Makeup
		99,
		100,
		101,
		102,
		103, // Tools -> Beauty
		106,
		108,
		109,
		110,
		111,
		158, //Pet Beauty -> Clipping
		159,
		160,
		161, //Pet Beauty -> Grooming
		161,
		163,
	];

	if (!category || noContent.includes(category)) {
		return undefined;
	}
	if (contentInMl.includes(category)) {
		return 'ml';
	}
	return 'g';
};

export const createInitialValues = (
	variations: IProduct[],
	variantParentId: string,
	categories: ICategoryDomainValue[],
): IProductForm => {
	if (variantParentId) {
		variations = variations.filter((v) => v.variantParentId === variantParentId);
	}
	if (!variations?.length) {
		if (variantParentId) {
			return null;
		}
		return {
			variations: [{}],
		};
	}
	variations.forEach((v) => (v.attributes = upperFirstObjectPropertiesNames(v.attributes)));
	const first: Omit<IProductForm, 'variations'> = { ...variations[0] };
	if (categories?.length && first?.attributes?.CategoryKey && !first.firstDropdownCategoryKey) {
		let cat = Number(first.attributes.CategoryKey);
		const categoriesDescending: number[] = [];
		while (cat) {
			categoriesDescending.push(cat);
			cat = categories.find((x) => x.key === cat)?.parentKey;
		}
		const keys: Array<keyof IProductForm> = [
			'firstDropdownCategoryKey',
			'secondDropdownCategoryKey',
			'thirdDropdownCategoryKey',
		];
		while (categoriesDescending.length && keys.length) {
			first[keys.shift()] = categoriesDescending.pop();
		}
	}
	return {
		...first,
		variations,
		hasVariations: variations.length > 1,
	};
};
